import { yupResolver } from '@hookform/resolvers/yup'
import { getMessage } from '@limitless-minds/app-messages'
import { Button } from '@limitless-minds/ui-button'
import { Input } from '@limitless-minds/ui-input'
import { Paragraph } from '@limitless-minds/ui-text'
import CheckIcon from '@mui/icons-material/Check'
import { Stack, styled, useTheme } from '@mui/material'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'

import { base as baseConfig } from '@/config'
import { useResetPassword } from '@/data/auth'
import { PATHS } from '@/router/constants'
import { Error as IError } from '@/types/Error'
import ErrorMessage from '@/ui/atoms/ErrorMessage'
import StyledLink from '@/ui/atoms/StyledLink'
import Footer from '@/ui/molecules/Footer'
import HeaderWrapper from '@/ui/molecules/HeaderWrapper'
import InfoComponent from '@/ui/organisms/InfoComponent'

import { FIELDS, schema } from './schema'

const StyledDiv = styled(Stack)(`
  justify-content: center;
  align-items: center;
  align-self: center;
  width: 100%;
  height: 100%;
  gap: 2rem;
`)
const StyledParagraph = styled(Paragraph)`
  text-decoration-line: line-through;
`
type FormValues = {
  password: string
  repeatPassword: string
}

interface DecodedToken extends JwtPayload {
  userId?: string
}

export const ResetPasswordTemplate: React.FC = () => {
  const [failedMessage, setFailedMessage] = useState('')
  const [showError, setShowError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const theme = useTheme()

  const { mutate: resetPassword } = useResetPassword()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setShowError(true)
      setIsLoading(false)
    } else {
      try {
        const tokenStr = Array.isArray(token) ? token[0] : token
        const decoded = jwtDecode<DecodedToken>(tokenStr)
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          throw new Error('TokenExpiredError')
        }
        setIsLoading(false)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error: Error | any) {
        setIsLoading(false)
        setShowError(true)
      }
    }
  }, [token])

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      [FIELDS.PASSWORD]: '',
      [FIELDS.REPEAT_PASSWORD]: '',
    },
  })

  const currentPassword = useWatch({ name: FIELDS.PASSWORD, control })
  const repeatPassword = useWatch({ name: FIELDS.REPEAT_PASSWORD, control })

  const hasMinLength = currentPassword?.length >= 8
  const hasSymbol = /[!@#$%^&()_+\-=[\]{};':"\\|,.<>/?]/.test(currentPassword || '')
  const has12Chars = currentPassword?.length >= 12
  const passedRules = [hasMinLength, hasSymbol, has12Chars].filter(Boolean).length

  const isPasswordValid = passedRules >= 2 && currentPassword === repeatPassword

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    (data: FormValues) => {
      const { password } = data
      resetPassword(
        { body: { password, token } },
        {
          onSuccess: () => {
            setFailedMessage('')
            window.location.href = `${baseConfig.webAppBaseUrl}`
          },
          onError: (error) => {
            const errorResponse = error as IError
            const errorMessage = getMessage(
              errorResponse.response?.data?.error?.errorCode,
              errorResponse.response?.data?.error?.params
            )
            setFailedMessage(errorMessage)
          },
        }
      )
    },
    [resetPassword, token]
  )

  return isLoading ? (
    <></>
  ) : showError ? (
    <StyledDiv maxWidth='31.25rem'>
      <InfoComponent
        title='This link is invalid'
        description='The link sent to your email expired or is invalid, please request a new reset password link.'
        image='/images/whistle.svg'
        imageAlt='Alert Illustration'
        buttonMessage='Go to Forgot Password'
        buttonOnClick={() => navigate(PATHS.FORGOT_PASSWORD)}
      />
    </StyledDiv>
  ) : (
    <>
      <StyledDiv key={'forgot-password-step1'} maxWidth='25rem'>
        <HeaderWrapper
          title='Set a new password'
          description={
            'You’ll be prompted to enter this new password in any other devices where you have an open session.'
          }
        />
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', gap: '2rem', width: '100%' }}
        >
          <Stack gap='0.75rem'>
            {failedMessage !== '' && <ErrorMessage>{failedMessage}</ErrorMessage>}
            <Controller
              name={FIELDS.PASSWORD}
              control={control}
              render={({ field }) => {
                const currentPassword = field.value ?? ''
                const getPasswordQuality = (): string => {
                  if (currentPassword.length === 0) return ''
                  if (currentPassword.length < 8) return 'Too short'
                  if (passedRules === 1) return 'Weak'
                  if (passedRules === 2) return 'Good'
                  if (passedRules >= 3) return 'Strong'
                  return ''
                }

                const getBarColor = (): string => {
                  // Three roles for four steps, and nothing is lost: the bar's WIDTH and the label
                  // beside it carry the fourth, so colour is the third channel rather than the only one.
                  //
                  // What each value owes is 3.0 against the TRACK it sits on, not against the card behind
                  // it - a state indicator under SC 1.4.11 is measured against what surrounds it. The
                  // three roles read 5.33 at worst across both modes, and `roles.ts` declares the pairs
                  // so a future move to any of them fails the build.
                  if (currentPassword.length === 0) return 'transparent'
                  if (currentPassword.length < 8) return theme.palette.role.error
                  if (passedRules === 1) return theme.palette.role.warning
                  if (passedRules >= 2) return theme.palette.role.success
                  return 'transparent'
                }

                const criteria = [
                  { text: 'Create a password with at least 8 characters', valid: hasMinLength },
                  { text: 'Use at least 1 symbol (/[!@#$%^&*(),.?":{}|<>]/)', valid: hasSymbol },
                  { text: '(Optional) Use 12 characters or more', valid: has12Chars },
                ]
                return (
                  <>
                    <Input
                      label={'Password'}
                      {...field}
                      password
                      error={errors?.[FIELDS.PASSWORD] ? true : false}
                      errorMessage={errors?.[FIELDS.PASSWORD]?.message}
                    />
                    <div
                      style={{
                        height: 6,
                        // The track is a surface, so it follows the mode: a literal here is a fixed
                        // pale band across whatever card it lands on.
                        backgroundColor: theme.palette.role.outlineVariant,
                        borderRadius: 4,
                        overflow: 'hidden',
                        marginTop: 6,
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${Math.max((passedRules / 3) * 100, 5)}%`,
                          backgroundColor: getBarColor(),
                          transition: 'width 0.3s',
                        }}
                      />
                    </div>

                    <div style={{ marginTop: 4, fontSize: 14 }}>
                      Password quality: <strong>{getPasswordQuality()}</strong>
                    </div>

                    {criteria.map(({ text, valid }) =>
                      valid ? (
                        <Stack direction='row' alignItems='center' spacing={1} key={text}>
                          <CheckIcon />
                          <StyledParagraph key={text} color={theme.palette.role.onSurfaceMuted}>
                            {text}
                          </StyledParagraph>
                        </Stack>
                      ) : (
                        <Stack direction='row' alignItems='center' spacing={1} key={text}>
                          <CheckIcon sx={{ color: theme.palette.role.primary }} />
                          <Paragraph key={text} color={theme.palette.role.onSurface}>
                            {text}
                          </Paragraph>
                        </Stack>
                      )
                    )}
                  </>
                )
              }}
            />
            <Controller
              name={FIELDS.REPEAT_PASSWORD}
              control={control}
              render={({ field }) => (
                <Input
                  label={'Confirm Password'}
                  {...field}
                  password
                  error={errors?.[FIELDS.REPEAT_PASSWORD] ? true : false}
                  errorMessage={errors?.[FIELDS.REPEAT_PASSWORD]?.message}
                />
              )}
            />
          </Stack>
          <Button type='submit' width='100%' disabled={!isPasswordValid}>
            Set Password
          </Button>
        </form>
      </StyledDiv>
      <Footer>
        Already have an account? <StyledLink to='/login'>Log In</StyledLink>
      </Footer>
    </>
  )
}
